import React, { useState, useMemo, useEffect, useRef } from "react";
import swal from "sweetalert";
import {
  Row,
  Col,
  Card,
  Button,
  Badge,
  Modal,
  Form,
  InputGroup,
  ProgressBar,
  Table,
  Spinner,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTasks,
  faPlus,
  faCalendarAlt,
  faClock,
  faSearch,
  faCheckCircle,
  faExclamationTriangle,
  faCommentAlt,
  faColumns,
  faListUl,
  faHistory,
  faTimes,
  faFolderOpen,
  faSyncAlt,
  faFlag,
  faEdit,
  faHourglassHalf,
  faUser,
  faPlay,
  faPause,
} from "@fortawesome/free-solid-svg-icons";
import {
  useTaskManager,
  formatDisplayDate,
  toApiStatus,
} from "./TaskManagementContext";
import { updateTask as updateTaskAPI } from "../../Service/AllTaskTrackerAPI";

export const EmployeeTaskManagement = ({ currentEmployee }) => {
  const {
    projects,
    tasks,
    createTask,
    updateTask,
    addWorkNote,
    fetchEmployeeTasks,
  } = useTaskManager();

  const employeeId = sessionStorage.getItem("pkUserId") || currentEmployee?.id || sessionStorage.getItem("userId") || sessionStorage.getItem("userName") || "af82e353-d160-45d0-9c21-880b072cdb69";
  const employeeName = currentEmployee?.name || [sessionStorage.getItem("firstName"), sessionStorage.getItem("lastName")].filter(Boolean).join(" ") || sessionStorage.getItem("userName") || "Employee";
  const employeeRole = currentEmployee?.role || sessionStorage.getItem("designation") || sessionStorage.getItem("role") || "Employee";

  // Auto-fetch employee's real tasks from API: GET /tasks/taskListById?pkUserId=...
  const hasFetchedEmployeeTasksRef = useRef(false);
  useEffect(() => {
    if (!hasFetchedEmployeeTasksRef.current && typeof fetchEmployeeTasks === "function" && employeeId) {
      hasFetchedEmployeeTasksRef.current = true;
      fetchEmployeeTasks(employeeId);
    }
  }, [fetchEmployeeTasks, employeeId]);

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProjectFilter, setSelectedProjectFilter] = useState("ALL");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("ALL");
  const [selectedPriorityFilter, setSelectedPriorityFilter] = useState("ALL");
  const [selectedOriginFilter, setSelectedOriginFilter] = useState("ALL"); // 'ALL' | 'MY_CREATED' | 'ASSIGNED'
  const [viewMode, setViewMode] = useState("KANBAN"); // 'KANBAN' | 'LIST'

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Create Task Form State
  const [newTaskProjectId, setNewTaskProjectId] = useState(projects[0] ? projects[0].id : (projects.length === 0 ? "PRJ-GEN" : ""));

  useEffect(() => {
    if ((!newTaskProjectId || newTaskProjectId === "PRJ-GEN") && projects && projects.length > 0) {
      setNewTaskProjectId(projects[0].id);
    }
  }, [projects, newTaskProjectId]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDesc, setNewTaskDesc] = useState("");
  const [newTaskStartDate, setNewTaskStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [newTaskDeadline, setNewTaskDeadline] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("Medium");
  const [newTaskInitialNote, setNewTaskInitialNote] = useState("");
  const [createValidated, setCreateValidated] = useState(false);

  // Task Detail & Update State
  const [detailTitle, setDetailTitle] = useState("");
  const [detailDesc, setDetailDesc] = useState("");
  const [detailProjectId, setDetailProjectId] = useState("");
  const [detailStatus, setDetailStatus] = useState("In Progress");
  const [detailProgress, setDetailProgress] = useState(0);
  const [detailPriority, setDetailPriority] = useState("Medium");
  const [detailStartDate, setDetailStartDate] = useState("");
  const [detailDueDate, setDetailDueDate] = useState("");
  const [newWorkNote, setNewWorkNote] = useState("");
  const [noteSuccessAlert, setNoteSuccessAlert] = useState(false);
  const [isUpdatingTask, setIsUpdatingTask] = useState(false);

  // All personal tasks for this employee directly from API or assigned
  const allEmployeeTasks = useMemo(() => {
    const curPk = String(sessionStorage.getItem("pkUserId") || "").toLowerCase().trim();
    const curId = String(employeeId || "").toLowerCase().trim();
    const curName = String(employeeName || "").toLowerCase().trim();
    const curUsername = String(sessionStorage.getItem("userName") || "").toLowerCase().trim();

    return tasks.filter((task) => {
      // If task was directly retrieved by employee API, match it immediately!
      if (task.isEmployeeTask) return true;

      const tId = String(task.assigneeId || "").toLowerCase().trim();
      const tAssigned = String(task.assignedTo || "").toLowerCase().trim();
      const tName = String(task.assigneeName || "").toLowerCase().trim();
      const tUser = String(task.assigneeUserName || "").toLowerCase().trim();

      const isMatch =
        (curPk && (tId === curPk || tAssigned === curPk || tId.includes(curPk) || tAssigned.includes(curPk))) ||
        (curId && (tId === curId || tAssigned === curId || tId.includes(curId) || curId.includes(tId))) ||
        (curName && (tName.includes(curName) || curName.includes(tName) || tAssigned.includes(curName) || curName.includes(tAssigned))) ||
        (curUsername && (tAssigned === curUsername || tUser === curUsername || tId === curUsername || tAssigned.includes(curUsername) || curUsername.includes(tAssigned)));

      return Boolean(isMatch);
    });
  }, [tasks, employeeId, employeeName]);

  // Personal statistics computed from ALL employee tasks (NEVER collapsed by selectedStatusFilter!)
  const stats = useMemo(() => {
    let pending = 0;
    let inProgress = 0;
    let completed = 0;
    let onHold = 0;
    let cancelled = 0;
    let overdue = 0;
    let myCreated = 0;

    allEmployeeTasks.forEach((t) => {
      if (t.isOverdue) overdue++;
      if (t.status === "Pending") pending++;
      else if (t.status === "In Progress") inProgress++;
      else if (t.status === "Completed") completed++;
      else if (t.status === "On Hold") onHold++;
      else if (t.status === "Cancelled") cancelled++;
      else inProgress++;

      if (t.createdByType === "EMPLOYEE") myCreated++;
    });

    return {
      total: allEmployeeTasks.length,
      pending,
      inProgress,
      completed,
      onHold,
      cancelled,
      overdue,
      myCreated,
      managerAssigned: allEmployeeTasks.length - myCreated,
    };
  }, [allEmployeeTasks]);

  // Filtered personal tasks for Board & List view
  const myTasks = useMemo(() => {
    return allEmployeeTasks.filter((task) => {
      // Project filter
      if (selectedProjectFilter !== "ALL") {
        const isMatch =
          String(task.projectId) === String(selectedProjectFilter) ||
          String(task.projectName).toLowerCase() === String(selectedProjectFilter).toLowerCase() ||
          (projects.find((p) => String(p.id) === String(selectedProjectFilter))?.name === task.projectName);
        if (!isMatch) return false;
      }

      // Status filter
      if (selectedStatusFilter !== "ALL") {
        if (selectedStatusFilter === "Overdue") {
          if (!task.isOverdue) return false;
        } else if (task.status !== selectedStatusFilter) {
          return false;
        }
      }

      // Priority filter
      if (selectedPriorityFilter !== "ALL" && task.priority !== selectedPriorityFilter) {
        return false;
      }

      // Origin filter (Employee created vs Manager assigned)
      if (selectedOriginFilter === "MY_CREATED" && task.createdByType !== "EMPLOYEE") {
        return false;
      }
      if (selectedOriginFilter === "ASSIGNED" && task.createdByType === "EMPLOYEE") {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesTitle = (task.title || "").toLowerCase().includes(query);
        const matchesId = (task.id || "").toLowerCase().includes(query);
        const matchesDesc = (task.description || "").toLowerCase().includes(query);
        const matchesProject = (task.projectName || "").toLowerCase().includes(query);
        if (!matchesTitle && !matchesId && !matchesDesc && !matchesProject) {
          return false;
        }
      }

      return true;
    });
  }, [
    allEmployeeTasks,
    selectedProjectFilter,
    selectedStatusFilter,
    selectedPriorityFilter,
    selectedOriginFilter,
    searchQuery,
    projects,
  ]);

  // Handle open details modal
  const handleOpenDetailModal = (task) => {
    setSelectedTask(task);
    setDetailTitle(task.title || task.taskName || "");
    setDetailDesc(task.description || "");
    setDetailProjectId(task.projectId || (projects[0]?.id || 1));
    setDetailStatus(task.status || "Pending");
    setDetailProgress(task.progress || 0);
    setDetailPriority(task.priority || "Medium");
    setDetailStartDate(task.startDate || new Date().toISOString().split("T")[0]);
    setDetailDueDate(task.dueDate || task.deadline || task.endDate || new Date().toISOString().split("T")[0]);
    setNewWorkNote("");
    setNoteSuccessAlert(false);
    setShowDetailModal(true);
  };

  // Handle Create My Task submit (POST /tasks/createTask)
  const handleCreateSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newTaskTitle.trim() || !newTaskProjectId) {
      setCreateValidated(true);
      return;
    }

    try {
      const empUsername =
        sessionStorage.getItem("userName") ||
        (employeeName ? employeeName.toLowerCase().replace(/\s+/g, ".") : "sebestin.saldana");

      const matchedP = (projects || []).find(
        (p) => String(p.id) === String(newTaskProjectId) || String(p.rawId) === String(newTaskProjectId)
      );
      const chosenProjectName = matchedP?.name || matchedP?.projectName || "General Project";

      await createTask({
        projectId: newTaskProjectId,
        projectName: chosenProjectName,
        title: newTaskTitle.trim(),
        taskName: newTaskTitle.trim(),
        description: newTaskDesc.trim(),
        assigneeId: employeeName,
        assigneeName: employeeName,
        assignedTo: empUsername,
        assigneeUserName: empUsername,
        createdByType: "EMPLOYEE",
        createdById: employeeId,
        createdByName: employeeName,
        startDate: newTaskStartDate || new Date().toISOString().split("T")[0],
        deadline: newTaskDeadline,
        endDate: newTaskDeadline,
        dueDate: newTaskDeadline,
        priority: newTaskPriority,
        status: "Pending", // Always starts from pending
        progress: 0,
        initialNote: newTaskInitialNote.trim(),
      });
    } catch (err) {
      console.warn("Create task error:", err);
    }

    // Refresh employee tasks from server
    if (typeof fetchEmployeeTasks === "function") {
      fetchEmployeeTasks(employeeId);
    }

    // Reset Form
    setNewTaskTitle("");
    setNewTaskDesc("");
    setNewTaskStartDate(new Date().toISOString().split("T")[0]);
    setNewTaskDeadline("");
    setNewTaskPriority("Medium");
    setNewTaskInitialNote("");
    setCreateValidated(false);
    setShowCreateModal(false);
  };

  // Quick Status Change Handler (from Kanban Cards or Table Row)
  const handleQuickStatusChange = async (taskId, newStatus) => {
    const existingTask = allEmployeeTasks.find(
      (t) => t.id === taskId || String(t.rawId) === String(taskId)
    );

    let progressUpdate;
    if (newStatus === "Completed") {
      progressUpdate = 100;
    } else if (newStatus === "In Progress") {
      progressUpdate = existingTask && existingTask.progress > 0 ? existingTask.progress : 25;
    } else if (newStatus === "Pending") {
      progressUpdate = 0;
    } else {
      progressUpdate = existingTask ? existingTask.progress : 0;
    }

    try {
      if (typeof updateTask === "function") {
        await updateTask(taskId, {
          status: newStatus,
          progress: progressUpdate,
        });
      } else {
        await updateTaskAPI(taskId, {
          status: toApiStatus(newStatus),
          progress: progressUpdate,
        });
      }

      if (typeof fetchEmployeeTasks === "function") {
        fetchEmployeeTasks(employeeId);
      }
    } catch (err) {
      console.warn("Quick status update error:", err);
      swal("Error", err?.response?.data?.message || err?.message || "Failed to update task status.", "error");
    }
  };

  // Handle Save Work Note, Status & Date Updates (Calls PUT /tasks/updateTask/{id})
  const handleSaveNoteAndStatus = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedTask) return;
    setIsUpdatingTask(true);

    const taskId = selectedTask.rawId || selectedTask.id;

    const matchedP = (projects || []).find(
      (p) => String(p.id) === String(detailProjectId) || String(p.rawId) === String(detailProjectId)
    );
    const chosenProjectName = matchedP?.name || matchedP?.projectName || selectedTask.projectName || "General Project";

    const updates = {
      title: detailTitle.trim() || selectedTask.title,
      taskName: detailTitle.trim() || selectedTask.title,
      description: detailDesc.trim(),
      projectId: detailProjectId || selectedTask.projectId || 1,
      projectName: chosenProjectName,
      status: detailStatus,
      progress: detailProgress,
      priority: detailPriority,
      startDate: detailStartDate,
      dueDate: detailDueDate,
      deadline: detailDueDate,
      endDate: detailDueDate,
      assignedTo: selectedTask.assignedTo || selectedTask.assigneeUserName || "sebestin.saldana",
    };

    try {
      if (newWorkNote.trim()) {
        addWorkNote(selectedTask.id, {
          text: newWorkNote.trim(),
          author: employeeName,
          authorRole: employeeRole,
          progress: detailProgress,
          status: detailStatus,
        });
      }

      let res;
      if (typeof updateTask === "function") {
        res = await updateTask(taskId, updates);
      } else {
        res = await updateTaskAPI(taskId, updates);
      }

      // Refresh selectedTask object locally
      setSelectedTask((prev) => ({
        ...prev,
        ...updates,
        workNotes: newWorkNote.trim()
          ? [
            ...(prev.workNotes || []),
            {
              id: `NOTE-${Date.now()}`,
              author: employeeName,
              authorRole: employeeRole,
              timestamp: new Date().toISOString(),
              text: newWorkNote.trim(),
              progressSnapshot: detailProgress,
              statusSnapshot: detailStatus,
            },
          ]
          : prev.workNotes,
      }));

      // Refresh tasks from server
      if (typeof fetchEmployeeTasks === "function") {
        fetchEmployeeTasks(employeeId);
      }

      setNewWorkNote("");
      setNoteSuccessAlert(true);
      swal("Success", res?.message || "Task updated successfully.", "success");
      setTimeout(() => setNoteSuccessAlert(false), 3000);
    } catch (err) {
      console.warn("Update task error:", err);
      swal("Error", err?.response?.data?.message || err?.message || "Failed to update task.", "error");
    } finally {
      setIsUpdatingTask(false);
    }
  };

  // Priority color styling (Low, Medium, High)
  const renderPriorityBadge = (priority) => {
    switch (priority) {
      case "Urgent":
      case "High":
        return <span className="badge-priority-high"><FontAwesomeIcon icon={faFlag} className="me-1" />High</span>;
      case "Medium":
        return <span className="badge-priority-medium"><FontAwesomeIcon icon={faFlag} className="me-1" />Medium</span>;
      default:
        return <span className="badge-priority-low"><FontAwesomeIcon icon={faFlag} className="me-1" />Low</span>;
    }
  };

  // Status color styling
  const renderStatusBadge = (task) => {
    if (task.isOverdue && task.status !== "Completed" && task.status !== "Cancelled") {
      return (
        <span className="badge-task-overdue">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
          Overdue ({task.status})
        </span>
      );
    }
    switch (task.status) {
      case "Pending":
        return (
          <span className="badge-task-pending">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            Pending
          </span>
        );
      case "In Progress":
        return (
          <span className="badge-task-inprogress">
            <FontAwesomeIcon icon={faSyncAlt} spin className="me-1" />
            In Progress
          </span>
        );
      case "Completed":
        return (
          <span className="badge-task-completed">
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            Completed
          </span>
        );
      case "On Hold":
        return (
          <span className="badge-task-onhold">
            <FontAwesomeIcon icon={faPause} className="me-1" />
            On Hold
          </span>
        );
      case "Cancelled":
        return (
          <span className="badge-task-cancelled">
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Cancelled
          </span>
        );
      default:
        return <Badge bg="secondary">{task.status}</Badge>;
    }
  };

  // Source Origin badge
  const renderOriginBadge = (task) => {
    if (task.createdByType === "EMPLOYEE") {
      return (
        <span className="badge-origin-employee" title="Created by you under project">
          💡 My Created Task
        </span>
      );
    }
    return (
      <span className="badge-origin-manager" title={`Assigned by Manager: ${task.createdByName || "Operations"}`}>
        👔 Manager Assigned
      </span>
    );
  };

  return (
    <div className="employee-task-management-wrapper mt-3">
      {/* TOP DASHBOARD METRICS: PERSONAL WORKLOAD SUMMARY */}
      <div className="task-dashboard-hero mb-3">
        <Row className="g-2">
          {/* 1. Pending Tasks */}
          <Col xs={6} lg={3}>
            <div
              className={`task-kpi-card card-kpi-pending ${selectedStatusFilter === "Pending" ? "active-kpi" : ""}`}
              onClick={() => setSelectedStatusFilter(selectedStatusFilter === "Pending" ? "ALL" : "Pending")}
              role="button"
              title="Click to filter pending tasks"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="task-kpi-label">Pending / To Do</div>
                  <div className="task-kpi-sub">Queued to start</div>
                </div>
                <div className="task-kpi-val text-warning mb-0">{stats.pending}</div>
              </div>
            </div>
          </Col>

          {/* 2. In Progress Tasks */}
          <Col xs={6} lg={3}>
            <div
              className={`task-kpi-card card-kpi-inprogress ${selectedStatusFilter === "In Progress" ? "active-kpi" : ""}`}
              onClick={() => setSelectedStatusFilter(selectedStatusFilter === "In Progress" ? "ALL" : "In Progress")}
              role="button"
              title="Click to filter in-progress tasks"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="task-kpi-label">In Progress</div>
                  <div className="task-kpi-sub">Actively working</div>
                </div>
                <div className="task-kpi-val text-info mb-0">{stats.inProgress}</div>
              </div>
            </div>
          </Col>

          {/* 3. On Hold Tasks */}
          <Col xs={6} lg={3}>
            <div
              className={`task-kpi-card card-kpi-pending ${selectedStatusFilter === "On Hold" ? "active-kpi" : ""}`}
              onClick={() => setSelectedStatusFilter(selectedStatusFilter === "On Hold" ? "ALL" : "On Hold")}
              role="button"
              title="Click to filter on-hold tasks"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="task-kpi-label">On Hold</div>
                  <div className="task-kpi-sub">Paused / waiting</div>
                </div>
                <div className="task-kpi-val text-secondary mb-0" style={{ color: "#ea580c" }}>{stats.onHold}</div>
              </div>
            </div>
          </Col>

          {/* 4. Completed Tasks */}
          <Col xs={6} lg={3}>
            <div
              className={`task-kpi-card card-kpi-completed ${selectedStatusFilter === "Completed" ? "active-kpi" : ""}`}
              onClick={() => setSelectedStatusFilter(selectedStatusFilter === "Completed" ? "ALL" : "Completed")}
              role="button"
              title="Click to filter completed tasks"
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="task-kpi-label">Completed</div>
                  <div className="task-kpi-sub">Shipped &amp; closed</div>
                </div>
                <div className="task-kpi-val text-success mb-0">{stats.completed}</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* TOOLBAR & CONTROLS: ACTION BUTTONS + FILTER BAR */}
      <Card className="task-toolbar-card mb-3 border-0 shadow-sm">
        <Card.Body className="p-3">
          <Row className="g-2 align-items-center justify-content-between">
            {/* Left: Search & Filter Dropdowns */}
            <Col xs={12} xl={8}>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {/* Search Input */}
                <div className="task-search-input-wrap">
                  <InputGroup size="sm">
                    <InputGroup.Text className="bg-white border-end-0">
                      <FontAwesomeIcon icon={faSearch} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search my tasks..."
                      className="border-start-0 ps-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="border-start-0"
                        onClick={() => setSearchQuery("")}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    )}
                  </InputGroup>
                </div>

                {/* Filter by Project */}
                <Form.Select
                  size="sm"
                  className="task-filter-select"
                  value={selectedProjectFilter}
                  onChange={(e) => setSelectedProjectFilter(e.target.value)}
                >
                  <option value="ALL">📁 All Projects ({projects.length})</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Form.Select>

                {/* Filter by Status */}
                <Form.Select
                  size="sm"
                  className="task-filter-select"
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                >
                  <option value="ALL">Status: All</option>
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Overdue">⚠️ Overdue Only</option>
                </Form.Select>

                {/* Filter by Priority */}
                <Form.Select
                  size="sm"
                  className="task-filter-select"
                  value={selectedPriorityFilter}
                  onChange={(e) => setSelectedPriorityFilter(e.target.value)}
                >
                  <option value="ALL">Priority: All</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </Form.Select>

                {/* Filter by Origin */}
                <Form.Select
                  size="sm"
                  className="task-filter-select"
                  value={selectedOriginFilter}
                  onChange={(e) => setSelectedOriginFilter(e.target.value)}
                >
                  <option value="ALL">Source: All</option>
                  <option value="MY_CREATED">💡 Created by Me</option>
                  <option value="ASSIGNED">👔 Manager Assigned</option>
                </Form.Select>

                {/* Reset Filters */}
                {(searchQuery ||
                  selectedProjectFilter !== "ALL" ||
                  selectedStatusFilter !== "ALL" ||
                  selectedPriorityFilter !== "ALL" ||
                  selectedOriginFilter !== "ALL") && (
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0 ms-1 fw-bold text-decoration-none"
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedProjectFilter("ALL");
                        setSelectedStatusFilter("ALL");
                        setSelectedPriorityFilter("ALL");
                        setSelectedOriginFilter("ALL");
                      }}
                    >
                      Reset
                    </Button>
                  )}
              </div>
            </Col>

            {/* Right: View Toggle & Create My Task Button */}
            <Col xs={12} xl={4} className="text-xl-end">
              <div className="d-flex align-items-center justify-content-xl-end gap-2">
                {/* View Mode Toggle: Kanban vs List */}
                <div className="btn-group btn-group-sm" role="group">
                  <Button
                    variant={viewMode === "KANBAN" ? "primary" : "outline-secondary"}
                    onClick={() => setViewMode("KANBAN")}
                    title="Kanban Board View (Grouped by Status)"
                  >
                    <FontAwesomeIcon icon={faColumns} className="me-1" />
                    Board
                  </Button>
                  <Button
                    variant={viewMode === "LIST" ? "primary" : "outline-secondary"}
                    onClick={() => setViewMode("LIST")}
                    title="List / Table View"
                  >
                    <FontAwesomeIcon icon={faListUl} className="me-1" />
                    List
                  </Button>
                </div>

                {/* Create My Task Action Button */}
                <Button
                  variant="success"
                  size="sm"
                  className="fw-bold d-inline-flex align-items-center gap-2 shadow-sm text-white"
                  onClick={() => setShowCreateModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Create My Task</span>
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* MAIN VIEW CONTENT: KANBAN BOARD OR LIST */}
      {viewMode === "KANBAN" ? (
        /* KANBAN BOARD GROUPED BY STATUS: PENDING, IN PROGRESS, ON HOLD, COMPLETED */
        <div className="task-kanban-board">
          <Row className="g-3">
            {/* COLUMN 1: PENDING */}
            <Col xs={12} md={6} xl={myTasks.some((t) => t.status === "Cancelled") ? 2 : 3}>
              <div className="kanban-column column-pending">
                <div className="kanban-column-header">
                  <div className="d-flex align-items-center gap-2">
                    <span className="kanban-col-dot dot-pending" />
                    <h6 className="mb-0 fw-bold">Pending</h6>
                  </div>
                  <Badge bg="warning" text="dark" pill>
                    {myTasks.filter((t) => t.status === "Pending").length}
                  </Badge>
                </div>
                <div className="kanban-cards-container">
                  {myTasks.filter((t) => t.status === "Pending").length === 0 ? (
                    <div className="kanban-empty-slot">No pending tasks</div>
                  ) : (
                    myTasks
                      .filter((t) => t.status === "Pending")
                      .map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onOpenDetails={() => handleOpenDetailModal(task)}
                          onQuickStatusChange={handleQuickStatusChange}
                          renderPriorityBadge={renderPriorityBadge}
                          renderStatusBadge={renderStatusBadge}
                          renderOriginBadge={renderOriginBadge}
                        />
                      ))
                  )}
                </div>
              </div>
            </Col>

            {/* COLUMN 2: IN PROGRESS */}
            <Col xs={12} md={6} xl={myTasks.some((t) => t.status === "Cancelled") ? 3 : 3}>
              <div className="kanban-column column-inprogress">
                <div className="kanban-column-header">
                  <div className="d-flex align-items-center gap-2">
                    <span className="kanban-col-dot dot-inprogress" />
                    <h6 className="mb-0 fw-bold">In Progress</h6>
                  </div>
                  <Badge bg="info" pill>
                    {myTasks.filter((t) => t.status === "In Progress").length}
                  </Badge>
                </div>
                <div className="kanban-cards-container">
                  {myTasks.filter((t) => t.status === "In Progress").length === 0 ? (
                    <div className="kanban-empty-slot">No tasks in progress</div>
                  ) : (
                    myTasks
                      .filter((t) => t.status === "In Progress")
                      .map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onOpenDetails={() => handleOpenDetailModal(task)}
                          onQuickStatusChange={handleQuickStatusChange}
                          renderPriorityBadge={renderPriorityBadge}
                          renderStatusBadge={renderStatusBadge}
                          renderOriginBadge={renderOriginBadge}
                        />
                      ))
                  )}
                </div>
              </div>
            </Col>

            {/* COLUMN 3: ON HOLD */}
            <Col xs={12} md={6} xl={myTasks.some((t) => t.status === "Cancelled") ? 2 : 3}>
              <div className="kanban-column column-pending" style={{ borderColor: "#fed7aa" }}>
                <div className="kanban-column-header">
                  <div className="d-flex align-items-center gap-2">
                    <span className="kanban-col-dot dot-onhold" />
                    <h6 className="mb-0 fw-bold">On Hold</h6>
                  </div>
                  <Badge bg="warning" text="dark" pill style={{ backgroundColor: "#ffedd5" }}>
                    {myTasks.filter((t) => t.status === "On Hold").length}
                  </Badge>
                </div>
                <div className="kanban-cards-container">
                  {myTasks.filter((t) => t.status === "On Hold").length === 0 ? (
                    <div className="kanban-empty-slot">No tasks on hold</div>
                  ) : (
                    myTasks
                      .filter((t) => t.status === "On Hold")
                      .map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onOpenDetails={() => handleOpenDetailModal(task)}
                          onQuickStatusChange={handleQuickStatusChange}
                          renderPriorityBadge={renderPriorityBadge}
                          renderStatusBadge={renderStatusBadge}
                          renderOriginBadge={renderOriginBadge}
                        />
                      ))
                  )}
                </div>
              </div>
            </Col>

            {/* COLUMN 4: COMPLETED */}
            <Col xs={12} md={6} xl={myTasks.some((t) => t.status === "Cancelled") ? 3 : 3}>
              <div className="kanban-column column-completed">
                <div className="kanban-column-header">
                  <div className="d-flex align-items-center gap-2">
                    <span className="kanban-col-dot dot-completed" />
                    <h6 className="mb-0 fw-bold">Completed</h6>
                  </div>
                  <Badge bg="success" pill>
                    {myTasks.filter((t) => t.status === "Completed").length}
                  </Badge>
                </div>
                <div className="kanban-cards-container">
                  {myTasks.filter((t) => t.status === "Completed").length === 0 ? (
                    <div className="kanban-empty-slot">No completed tasks yet</div>
                  ) : (
                    myTasks
                      .filter((t) => t.status === "Completed")
                      .map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onOpenDetails={() => handleOpenDetailModal(task)}
                          onQuickStatusChange={handleQuickStatusChange}
                          renderPriorityBadge={renderPriorityBadge}
                          renderStatusBadge={renderStatusBadge}
                          renderOriginBadge={renderOriginBadge}
                        />
                      ))
                  )}
                </div>
              </div>
            </Col>

            {/* COLUMN 5: CANCELLED (if any) */}
            {myTasks.some((t) => t.status === "Cancelled") && (
              <Col xs={12} md={6} xl={2}>
                <div className="kanban-column" style={{ borderTop: "3px solid #ef4444" }}>
                  <div className="kanban-column-header">
                    <div className="d-flex align-items-center gap-2">
                      <span className="kanban-col-dot dot-cancelled" />
                      <h6 className="mb-0 fw-bold">Cancelled</h6>
                    </div>
                    <Badge bg="danger" pill>
                      {myTasks.filter((t) => t.status === "Cancelled").length}
                    </Badge>
                  </div>
                  <div className="kanban-cards-container">
                    {myTasks
                      .filter((t) => t.status === "Cancelled")
                      .map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onOpenDetails={() => handleOpenDetailModal(task)}
                          onQuickStatusChange={handleQuickStatusChange}
                          renderPriorityBadge={renderPriorityBadge}
                          renderStatusBadge={renderStatusBadge}
                          renderOriginBadge={renderOriginBadge}
                        />
                      ))}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </div>
      ) : (
        /* LIST / TABLE VIEW */
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="table-responsive task-table-vertical-wrap">
              <div className="mobile-table-scroll-hint">
                <span>Swipe horizontally to view all task columns &rarr;</span>
              </div>
              <Table hover className="align-middle mb-0 task-table">
                <thead className="bg-light text-uppercase fs-7 text-muted">
                  <tr>
                    <th>Task ID & Title</th>
                    <th>Project</th>
                    <th>Source</th>
                    <th>Priority</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Progress</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myTasks.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-5 text-muted">
                        <FontAwesomeIcon icon={faTasks} className="fa-3x mb-3 text-secondary opacity-50" />
                        <p className="mb-0 fw-semibold">
                          {tasks.length === 0
                            ? "No tasks assigned to you yet. Click 'Create My Task' to add personal tasks."
                            : "No tasks match your filter criteria."}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    myTasks.map((task) => (
                      <tr key={task.id}>
                        <td>
                          <div className="fw-bold text-dark">{task.title}</div>
                          <div className="text-muted fs-7">
                            <span className="badge bg-light text-dark border me-1">{task.id}</span>
                            {task.tags && task.tags.slice(0, 2).join(", ")}
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-primary-soft text-primary fw-semibold">
                            📁 {task.projectName}
                          </span>
                        </td>
                        <td>{renderOriginBadge(task)}</td>
                        <td>{renderPriorityBadge(task.priority)}</td>
                        <td>
                          <div className="fs-7 text-dark fw-semibold">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-primary" />
                            {formatDisplayDate(task.startDate)}
                          </div>
                        </td>
                        <td>
                          <div className="fs-7 text-dark fw-semibold">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-danger" />
                            {formatDisplayDate(task.deadline || task.endDate)}
                          </div>
                          {task.isOverdue && task.status !== "Completed" && (
                            <span className="text-danger fs-8 fw-bold">⚠️ Overdue</span>
                          )}
                        </td>
                        <td style={{ minWidth: "110px" }}>
                          <div className="d-flex justify-content-between fs-8 fw-semibold mb-1">
                            <span>Progress</span>
                            <span className="text-primary">{task.progress}%</span>
                          </div>
                          <ProgressBar
                            now={task.progress}
                            variant={task.progress === 100 ? "success" : task.progress > 50 ? "info" : "warning"}
                            style={{ height: "6px" }}
                          />
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            className="task-quick-status-select py-1 px-2 fs-8 fw-bold"
                            value={task.status}
                            onChange={(e) => handleQuickStatusChange(task.id, e.target.value)}
                          >
                            <option value="Pending">⏳ Pending</option>
                            <option value="In Progress">🔄 In Progress</option>
                            <option value="On Hold">⏸️ On Hold</option>
                            <option value="Completed">✅ Done / Completed</option>
                            <option value="Cancelled">❌ Cancelled</option>
                          </Form.Select>
                        </td>
                        <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                          <div className="d-inline-flex align-items-center gap-1.5 justify-content-end">
                            {task.status === "Pending" && (
                              <Button
                                variant="primary"
                                size="sm"
                                className="d-inline-flex align-items-center gap-1 py-1 px-2 fs-8 fw-bold text-white shadow-sm"
                                onClick={() => handleQuickStatusChange(task.id, "In Progress")}
                                title="Start working on this task"
                              >
                                <FontAwesomeIcon icon={faPlay} />
                                <span>Start Task</span>
                              </Button>
                            )}
                            {task.status === "In Progress" && (
                              <Button
                                variant="success"
                                size="sm"
                                className="d-inline-flex align-items-center gap-1 py-1 px-2 fs-8 fw-bold text-white shadow-sm"
                                onClick={() => handleQuickStatusChange(task.id, "Completed")}
                                title="Complete this task"
                              >
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <span>Complete Task</span>
                              </Button>
                            )}
                            {task.status === "On Hold" && (
                              <Button
                                variant="info"
                                size="sm"
                                className="d-inline-flex align-items-center gap-1 py-1 px-2 fs-8 fw-bold text-white shadow-sm"
                                onClick={() => handleQuickStatusChange(task.id, "In Progress")}
                                title="Resume working on this task"
                              >
                                <FontAwesomeIcon icon={faPlay} />
                                <span>Resume</span>
                              </Button>
                            )}
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="btn-update-task d-inline-flex align-items-center gap-1 py-1 px-2 fs-8 fw-bold"
                              onClick={() => handleOpenDetailModal(task)}
                              title="Update task details and progress"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                              <span>Update Task</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* CREATE MY TASK MODAL */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Form onSubmit={handleCreateSubmit}>
          <Modal.Header closeButton className="tracker-modal-header bg-dark text-white">
            <Modal.Title className="fs-5 d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faPlus} className="text-success" />
              <span>Create My Task under Project</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="alert alert-info border-0 py-2 px-3 fs-7 mb-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                💡 <strong>Employee Self-Tasking:</strong> This task will be assigned to your queue and tracked.
              </div>
              {/* <span className="badge bg-warning text-dark border">
                Initial Status: ⏳ Pending (0%)
              </span> */}
            </div>

            <Row className="g-3">
              {/* Project Selection */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Select Project <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    required
                    value={newTaskProjectId}
                    onChange={(e) => setNewTaskProjectId(e.target.value)}
                  >
                    {projects.length === 0 ? (
                      <option value="PRJ-GEN">General / Direct Activities (Default)</option>
                    ) : (
                      projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} {p.code ? `(${p.code})` : ""}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Priority */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Priority</Form.Label>
                  <Form.Select
                    value={newTaskPriority}
                    onChange={(e) => setNewTaskPriority(e.target.value)}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Start Date */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Start Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={newTaskStartDate}
                    onChange={(e) => setNewTaskStartDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* End Date */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">End Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={newTaskDeadline}
                    onChange={(e) => setNewTaskDeadline(e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Task Title */}
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Task Title / Objective <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder="e.g. Implement MQTT client reconnection backoff retry logic..."
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    isInvalid={createValidated && !newTaskTitle.trim()}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a task title.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* Task Description */}
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-bold fs-7">Detailed Description & Deliverables</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Describe implementation details, scope, dependencies, or acceptance criteria..."
                    value={newTaskDesc}
                    onChange={(e) => setNewTaskDesc(e.target.value)}
                  />
                </Form.Group>
              </Col>



            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" size="sm" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="success"
              size="sm"
              type="button"
              className="fw-bold text-white px-4"
              onClick={handleCreateSubmit}
            >
              {/* <FontAwesomeIcon icon={faPlus} className="me-1" /> */}
              Submit
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* TASK DETAILS, STATUS UPDATE & WORK NOTES MODAL */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        {selectedTask && (
          <>
            <Modal.Header closeButton className="tracker-modal-header">
              <div className="d-flex align-items-center justify-content-between w-100 pe-2">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                    style={{
                      width: "44px",
                      height: "44px",
                      background: "linear-gradient(135deg, rgba(59, 130, 246, 0.35), rgba(37, 99, 235, 0.2))",
                      color: "#93c5fd",
                      border: "1px solid rgba(147, 197, 253, 0.3)",
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} className="fs-5" />
                  </div>
                  <div>
                    <Modal.Title className="fs-5 fw-bold text-white mb-0">Update Task Details</Modal.Title>
                    <small className="text-light opacity-75">
                      Modify task information, priority, schedule, and execution progress
                    </small>
                  </div>
                </div>
                <div className="d-none d-sm-flex align-items-center gap-2">
                  <span className="badge bg-secondary font-monospace px-2.5 py-1.5 fs-7 fw-bold">
                    {selectedTask.id}
                  </span>
                  {renderOriginBadge(selectedTask)}
                </div>
              </div>
            </Modal.Header>

            <Modal.Body className="p-4 bg-light bg-opacity-50">
              {/* Context Information Pill Banner */}
              <div className="p-3 mb-3 rounded-3 border bg-white d-flex flex-wrap align-items-center justify-content-between gap-2 shadow-sm">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2.5 py-1.5 fs-7 fw-semibold d-inline-flex align-items-center gap-1.5">
                    <FontAwesomeIcon icon={faFolderOpen} className="text-warning" />
                    <span>Project: <strong>{selectedTask.projectName}</strong></span>
                  </span>
                  <span className="d-inline-flex d-sm-none">
                    <span className="badge bg-secondary font-monospace px-2 py-1 fs-8">
                      {selectedTask.id}
                    </span>
                  </span>
                </div>
                <div className="d-flex align-items-center gap-3 fs-7 text-muted">
                  <span>
                    <FontAwesomeIcon icon={faUser} className="me-1 text-primary" />
                    Assignee: <strong className="text-dark">{selectedTask.assigneeName}</strong>
                  </span>
                  <span>{renderPriorityBadge(detailPriority)}</span>
                </div>
              </div>

              {/* Interactive Status Selector Cards */}
              <div className="mb-3">
                <Form.Label className="fw-bold fs-7 text-dark mb-2">
                  Task Execution Status <span className="text-danger">*</span>
                </Form.Label>
                <Row className="g-2">
                  {[
                    {
                      value: "Pending",
                      label: "Pending",
                      desc: "Queued to start",
                      icon: faClock,
                      activeColor: "#d97706",
                      bgActive: "#fffbeb",
                      borderActive: "#f59e0b",
                    },
                    {
                      value: "In Progress",
                      label: "In Progress",
                      desc: "Active work execution",
                      icon: faHourglassHalf,
                      activeColor: "#2563eb",
                      bgActive: "#eff6ff",
                      borderActive: "#3b82f6",
                    },
                    {
                      value: "On Hold",
                      label: "On Hold",
                      desc: "Paused / waiting",
                      icon: faPause,
                      activeColor: "#ea580c",
                      bgActive: "#fff7ed",
                      borderActive: "#f97316",
                    },
                    {
                      value: "Completed",
                      label: "Completed",
                      desc: "Finished & verified",
                      icon: faCheckCircle,
                      activeColor: "#059669",
                      bgActive: "#ecfdf5",
                      borderActive: "#10b981",
                    },
                    {
                      value: "Cancelled",
                      label: "Cancelled",
                      desc: "Discarded / stopped",
                      icon: faTimes,
                      activeColor: "#dc2626",
                      bgActive: "#fef2f2",
                      borderActive: "#ef4444",
                    },
                  ].map((st) => {
                    const isSelected = detailStatus === st.value;
                    return (
                      <Col xs={6} sm={4} lg={true} key={st.value}>
                        <div
                          onClick={() => {
                            setDetailStatus(st.value);
                            if (st.value === "Completed" && detailProgress < 100) {
                              setDetailProgress(100);
                            } else if (st.value === "In Progress" && detailProgress === 0) {
                              setDetailProgress(25);
                            } else if (st.value === "Pending" && detailProgress === 100) {
                              setDetailProgress(0);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          className="p-2 rounded-3 border text-center transition-all h-100"
                          style={{
                            cursor: "pointer",
                            borderColor: isSelected ? st.borderActive : "#e2e8f0",
                            backgroundColor: isSelected ? st.bgActive : "#ffffff",
                            boxShadow: isSelected ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-center gap-1.5 mb-1" style={{ color: isSelected ? st.activeColor : "#64748b" }}>
                            <FontAwesomeIcon icon={st.icon} />
                            <span className="fw-bold fs-7">{st.label}</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                            {st.desc}
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>

              {/* Core Task Fields Card */}
              <div className="p-3 mb-3 rounded-3 border bg-white shadow-sm">
                <Row className="g-3">
                  {/* Task Title */}
                  <Col xs={12} sm={8}>
                    <Form.Group>
                      <Form.Label className="fw-bold fs-7 text-dark mb-1">
                        Task Name / Title <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={detailTitle}
                        onChange={(e) => setDetailTitle(e.target.value)}
                        placeholder="Enter concise task title..."
                        required
                        className="fs-7 shadow-none"
                      />
                    </Form.Group>
                  </Col>

                  {/* Project Selection */}
                  <Col xs={12} sm={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold fs-7 text-dark mb-1">
                        <FontAwesomeIcon icon={faFolderOpen} className="me-1 text-warning" />
                        Project
                      </Form.Label>
                      <Form.Select
                        value={detailProjectId}
                        onChange={(e) => setDetailProjectId(e.target.value)}
                        className="fs-7 shadow-none"
                      >
                        {(projects && projects.length > 0 ? projects : [{ id: 1, name: selectedTask.projectName || "General Project" }]).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Priority */}
                  <Col xs={12} sm={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold fs-7 text-dark mb-1">
                        <FontAwesomeIcon icon={faFlag} className="me-1 text-danger" />
                        Priority Level
                      </Form.Label>
                      <Form.Select
                        value={detailPriority}
                        onChange={(e) => setDetailPriority(e.target.value)}
                        className="fs-7 shadow-none"
                      >
                        <option value="High">🔴 High Priority</option>
                        <option value="Medium">🟡 Medium Priority</option>
                        <option value="Low">🟢 Low Priority</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {/* Start Date */}
                  <Col xs={12} sm={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold fs-7 text-dark mb-1">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-primary" />
                        Start Date
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={detailStartDate}
                        onChange={(e) => setDetailStartDate(e.target.value)}
                        className="fs-7 shadow-none"
                      />
                    </Form.Group>
                  </Col>

                  {/* Due Date */}
                  <Col xs={12} sm={4}>
                    <Form.Group>
                      <Form.Label className="fw-bold fs-7 text-dark mb-1">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-danger" />
                        Due Date / Deadline
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={detailDueDate}
                        onChange={(e) => setDetailDueDate(e.target.value)}
                        className="fs-7 shadow-none"
                      />
                    </Form.Group>
                  </Col>

                  {/* Description */}
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold fs-7 text-dark mb-1">
                        Task Description &amp; Scope
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={detailDesc}
                        onChange={(e) => setDetailDesc(e.target.value)}
                        placeholder="Describe deliverables, requirements, or instructions..."
                        className="fs-7 shadow-none"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Execution Progress Card */}
              <div className="p-3 mb-3 rounded-3 border bg-white shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="fw-bold fs-7 text-dark mb-0">
                    Execution Progress
                  </Form.Label>
                  <Badge
                    bg={detailProgress === 100 ? "success" : detailProgress >= 50 ? "info" : detailProgress > 0 ? "warning" : "secondary"}
                    className="fs-7 px-2.5 py-1"
                  >
                    {detailProgress}% Completed
                  </Badge>
                </div>
                <ProgressBar
                  now={detailProgress}
                  variant={detailProgress === 100 ? "success" : detailProgress >= 50 ? "info" : "warning"}
                  style={{ height: "7px" }}
                  className="mb-2.5"
                />
                <Form.Range
                  min="0"
                  max="100"
                  step="5"
                  value={detailProgress}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setDetailProgress(val);
                    if (val === 100) setDetailStatus("Completed");
                    else if (val > 0 && detailStatus === "Pending") setDetailStatus("In Progress");
                  }}
                />
                <div className="d-flex justify-content-between align-items-center mt-2 pt-1 border-top">
                  <span className="text-muted fs-8">Quick Preset:</span>
                  <div className="d-flex gap-1">
                    {[0, 25, 50, 75, 100].map((pct) => (
                      <Button
                        key={pct}
                        variant={detailProgress === pct ? "primary" : "outline-secondary"}
                        size="sm"
                        className="py-0 px-2 fs-8 fw-semibold"
                        onClick={() => {
                          setDetailProgress(pct);
                          if (pct === 100) setDetailStatus("Completed");
                          else if (pct > 0 && detailStatus === "Pending") setDetailStatus("In Progress");
                          else if (pct === 0 && detailStatus === "Completed") setDetailStatus("Pending");
                        }}
                      >
                        {pct}%
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Work Note Input Card */}
              <div className="p-3 mb-3 rounded-3 border bg-white shadow-sm">
                <Form.Label className="fw-bold fs-7 text-dark mb-1 d-flex align-items-center gap-1.5">
                  <FontAwesomeIcon icon={faCommentAlt} className="text-primary" />
                  <span>Log Work Note / Activity Update (Optional)</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  className="fs-7 shadow-none"
                  placeholder="Log milestone achieved, blocker, PR link, or daily update..."
                  value={newWorkNote}
                  onChange={(e) => setNewWorkNote(e.target.value)}
                />
              </div>

              {/* Work Notes History Timeline */}
              <div className="p-3 rounded-3 border bg-white shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0 fs-7 d-flex align-items-center gap-2 text-dark">
                    <FontAwesomeIcon icon={faHistory} className="text-secondary" />
                    <span>Activity &amp; Work Notes History</span>
                  </h6>
                  <span className="badge bg-secondary bg-opacity-10 text-secondary border fs-8">
                    {selectedTask.workNotes ? selectedTask.workNotes.length : 0} notes
                  </span>
                </div>

                {(!selectedTask.workNotes || selectedTask.workNotes.length === 0) ? (
                  <div className="text-muted fs-8 py-2 text-center bg-light rounded border">
                    No previous work notes recorded yet for this task.
                  </div>
                ) : (
                  <div className="work-notes-timeline" style={{ maxHeight: "180px", overflowY: "auto" }}>
                    {[...selectedTask.workNotes].reverse().map((note) => (
                      <div key={note.id} className="timeline-note-item bg-light p-2.5 rounded border mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="fw-bold text-dark fs-8">
                            {note.author} <span className="text-muted fw-normal">({note.authorRole || "Employee"})</span>
                          </span>
                          <span className="text-muted fs-8">
                            {formatDisplayDate(note.timestamp)} {new Date(note.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        <p className="mb-1 text-dark fs-7 lh-sm">{note.text}</p>
                        <div className="d-flex gap-2 fs-8 text-muted">
                          {note.statusSnapshot && (
                            <span className="badge bg-white text-dark border py-0 px-1.5">
                              Status: {note.statusSnapshot}
                            </span>
                          )}
                          {note.progressSnapshot !== undefined && (
                            <span className="badge bg-white text-dark border py-0 px-1.5">
                              Progress: {note.progressSnapshot}%
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Modal.Body>

            <Modal.Footer className="border-top bg-light px-4 py-3 d-flex justify-content-between align-items-center">
              <div>
                {noteSuccessAlert && (
                  <span className="text-success fw-bold fs-7">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                    Task updated successfully!
                  </span>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="px-3"
                  onClick={() => setShowDetailModal(false)}
                  disabled={isUpdatingTask}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  className="fw-bold px-4 shadow-sm d-inline-flex align-items-center gap-2"
                  onClick={handleSaveNoteAndStatus}
                  disabled={isUpdatingTask || !detailTitle.trim()}
                >
                  {isUpdatingTask ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <span>Updating Task...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <span>Save &amp; Update Task</span>
                    </>
                  )}
                </Button>
              </div>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

// Sub-component: Task Card for Kanban Board
const TaskCard = ({
  task,
  onOpenDetails,
  onQuickStatusChange,
  renderPriorityBadge,
  renderStatusBadge,
  renderOriginBadge,
}) => {
  return (
    <div
      className={`task-kanban-card ${task.isOverdue && task.status !== "Completed" ? "card-is-overdue" : ""}`}
      onClick={onOpenDetails}
      role="button"
    >
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="badge bg-light text-dark border fs-8 fw-semibold">{task.id}</span>
        {renderPriorityBadge(task.priority)}
      </div>

      <h6 className="task-card-title mb-1 text-dark">{task.title}</h6>

      <div className="task-card-project mb-2">
        📁 <span className="text-primary fw-semibold">{task.projectName}</span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 fs-8">
        <span className="text-muted" title="Start Date">
          <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-primary" />
          {formatDisplayDate(task.startDate)}
        </span>
        <span className="text-muted">&rarr;</span>
        <span className={task.isOverdue && task.status !== "Completed" ? "text-danger fw-bold" : "text-dark fw-semibold"} title="End Date">
          <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-danger" />
          {formatDisplayDate(task.deadline || task.endDate)}
        </span>
      </div>

      {/* Progress */}
      <div className="mb-2">
        <div className="d-flex justify-content-between fs-8 fw-bold mb-1">
          <span>Progress</span>
          <span>{task.progress}%</span>
        </div>
        <ProgressBar
          now={task.progress}
          variant={task.progress === 100 ? "success" : task.progress > 50 ? "info" : "warning"}
          style={{ height: "5px" }}
        />
      </div>

      <div className="d-flex justify-content-between align-items-center pt-2 border-top">
        {renderOriginBadge(task)}
        <div className="d-flex align-items-center gap-2">
          <span className="fs-8 text-primary fw-bold">
            <FontAwesomeIcon icon={faCommentAlt} className="me-1" />
            {task.workNotes ? task.workNotes.length : 0} notes
          </span>
          <Button
            variant="outline-primary"
            size="sm"
            className="py-0 px-2 fs-8 fw-semibold d-inline-flex align-items-center gap-1"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails();
            }}
            title="Update task details"
          >
            <FontAwesomeIcon icon={faEdit} />
            <span>Update</span>
          </Button>
        </div>
      </div>

      {/* Quick Move / Status Transition Actions */}
      <div
        className="task-card-quick-actions pt-2 mt-2 border-top d-flex flex-wrap gap-1 align-items-center justify-content-between"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="fs-8 fw-bold text-muted">Action:</span>
        <div className="d-flex flex-wrap gap-1">
          {task.status === "Pending" && (
            <Button
              variant="primary"
              size="sm"
              className="py-1 px-2.5 fs-8 fw-bold text-white shadow-sm d-inline-flex align-items-center gap-1"
              onClick={() => onQuickStatusChange(task.id, "In Progress")}
              title="Start working on this task"
            >
              <FontAwesomeIcon icon={faPlay} />
              <span>Start Task &rarr;</span>
            </Button>
          )}
          {task.status === "In Progress" && (
            <>
              <Button
                variant="outline-warning"
                size="sm"
                className="py-1 px-2 fs-8 fw-bold text-dark d-inline-flex align-items-center gap-1"
                onClick={() => onQuickStatusChange(task.id, "On Hold")}
                title="Pause / put task On Hold"
              >
                <FontAwesomeIcon icon={faPause} />
                <span>On Hold</span>
              </Button>
              <Button
                variant="success"
                size="sm"
                className="text-white py-1 px-2.5 fs-8 fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                onClick={() => onQuickStatusChange(task.id, "Completed")}
                title="Complete this task"
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Complete Task &check;</span>
              </Button>
            </>
          )}
          {task.status === "On Hold" && (
            <>
              <Button
                variant="primary"
                size="sm"
                className="text-white py-1 px-2 fs-8 fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                onClick={() => onQuickStatusChange(task.id, "In Progress")}
                title="Resume working on this task"
              >
                <FontAwesomeIcon icon={faPlay} />
                <span>Resume &rarr;</span>
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                className="py-1 px-2 fs-8 fw-bold d-inline-flex align-items-center gap-1"
                onClick={() => onQuickStatusChange(task.id, "Cancelled")}
                title="Cancel this task"
              >
                <FontAwesomeIcon icon={faTimes} />
                <span>Cancel</span>
              </Button>
            </>
          )}
          {task.status === "Completed" && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="py-1 px-2 fs-8 fw-bold d-inline-flex align-items-center gap-1"
              onClick={() => onQuickStatusChange(task.id, "In Progress")}
              title="Reopen task"
            >
              <FontAwesomeIcon icon={faSyncAlt} />
              <span>Reopen</span>
            </Button>
          )}
          {task.status === "Cancelled" && (
            <Button
              variant="outline-primary"
              size="sm"
              className="py-1 px-2 fs-8 fw-bold d-inline-flex align-items-center gap-1"
              onClick={() => onQuickStatusChange(task.id, "Pending")}
              title="Restart task back to Pending"
            >
              <FontAwesomeIcon icon={faSyncAlt} />
              <span>Restart</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeTaskManagement;
